import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Loader2, Plus, CheckCircle2, Clock, Store, Truck, X, Upload } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { AuthImage } from "../components/AuthImage";

const STATUS = {
  PENDING: { label: "Menunggu Dicek Admin", cls: "bg-amber-100 text-amber-700", icon: Clock },
  QUOTED: { label: "Penawaran Siap", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  ACCEPTED: { label: "Diterima → Jadi Pesanan", cls: "bg-sky-100 text-sky-700", icon: CheckCircle2 },
  CANCELLED: { label: "Dibatalkan", cls: "bg-stone-100 text-stone-500", icon: X },
};

export default function Requests() {
  const [params] = useSearchParams();
  const [requests, setRequests] = useState(null);
  const [kiosks, setKiosks] = useState([]);
  const [form, setForm] = useState({ title: params.get("title") || "", source_url: params.get("url") || "", image_url: "", photo_path: "", qty: 1, notes: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [acceptFor, setAcceptFor] = useState(null);
  const [accept, setAccept] = useState({ fulfillment: "kedai", kiosk_id: "", address: "", phone: "", payment_method: "QRIS" });
  const nav = useNavigate();

  const load = () => api.get("/requests").then((r) => setRequests(r.data.requests));
  useEffect(() => {
    load();
    api.get("/kiosks").then((r) => { setKiosks(r.data.kiosks); if (r.data.kiosks[0]) setAccept((a) => ({ ...a, kiosk_id: r.data.kiosks[0].id })); });
  }, []);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads", fd);
      setForm((x) => ({ ...x, photo_path: data.path }));
      toast.success("Foto terunggah");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Isi nama/deskripsi barang");
    setSaving(true);
    try {
      await api.post("/requests", { ...form, qty: parseInt(form.qty) || 1 });
      toast.success("Permintaan terkirim! Admin akan cari & kroscek harga aslinya.");
      setForm({ title: "", source_url: "", image_url: "", photo_path: "", qty: 1, notes: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const doAccept = async () => {
    if (accept.fulfillment === "kedai" && !accept.kiosk_id) return toast.error("Pilih kedai");
    if (accept.fulfillment === "delivery" && !accept.address) return toast.error("Isi alamat");
    try {
      const { data } = await api.post(`/requests/${acceptFor}/accept`, accept);
      toast.success("Penawaran diterima! Pesanan dibuat.");
      setAcceptFor(null);
      nav(`/pesanan/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancel = async (id) => {
    try { await api.patch(`/requests/${id}/cancel`, {}); toast.success("Permintaan dibatalkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-2"><ClipboardList className="text-emerald-600" size={26} /><h1 className="font-display text-2xl font-bold text-stone-900">Permintaan Cek Harga</h1></div>
      <p className="text-stone-500 mb-6 text-sm">Minat sesuatu tapi harga di supplier suka tak akurat? Kirim permintaan, admin cari & kroscek harga aslinya, lalu dihitung ulang & ditawarkan ke kamu.</p>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-5 h-fit">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Plus size={18} />Buat Permintaan Baru</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-stone-600">Nama / Deskripsi Barang</label>
              <input data-testid="req-title" value={form.title} onChange={setF("title")} required placeholder="cth: Jam tangan pria anti air" className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-600">Foto Barang (upload)</label>
              <label className="mt-1 flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-500 cursor-pointer hover:border-emerald-400">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {form.photo_path ? "Ganti foto" : "Pilih foto dari galeri/kamera"}
                <input data-testid="req-photo" type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
              {form.photo_path && <AuthImage path={form.photo_path} className="mt-2 w-24 h-24 rounded-lg object-cover" />}
            </div>
            <div>
              <label className="text-sm font-medium text-stone-600">Link Produk (opsional)</label>
              <input data-testid="req-url" value={form.source_url} onChange={setF("source_url")} placeholder="Tempel link produk (opsional)" className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-600">Jumlah (pcs)</label>
              <input data-testid="req-qty" type="number" min="1" value={form.qty} onChange={setF("qty")} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-600">Catatan (warna, ukuran, dll)</label>
              <textarea data-testid="req-notes" value={form.notes} onChange={setF("notes")} rows={2} className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            </div>
            <button data-testid="req-submit" disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="animate-spin" size={16} />}Kirim Permintaan
            </button>
          </div>
        </form>

        {/* List */}
        <div className="lg:col-span-3">
          {!requests ? (
            <div className="py-16 flex justify-center text-emerald-600"><Loader2 className="animate-spin" size={28} /></div>
          ) : requests.length === 0 ? (
            <div className="py-16 text-center text-stone-400" data-testid="req-empty">Belum ada permintaan. Buat yang pertama di sebelah kiri.</div>
          ) : (
            <div className="space-y-3" data-testid="req-list">
              {requests.map((r) => {
                const st = STATUS[r.status] || STATUS.PENDING;
                return (
                  <div key={r.id} data-testid={`req-card-${r.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
                    <div className="flex items-start gap-3">
                      {r.photo_path ? <AuthImage path={r.photo_path} className="w-14 h-14 rounded-lg object-cover" /> : r.image_url ? <img src={r.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-stone-100 flex items-center justify-center text-stone-300"><ClipboardList size={20} /></div>}
                      <div className="flex-1">
                        <p className="font-medium text-stone-800 text-sm">{r.title}</p>
                        <p className="text-xs text-stone-400">{r.qty} pcs {r.notes && `· ${r.notes}`}</p>
                      </div>
                      <span className={`flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${st.cls}`}><st.icon size={12} />{st.label}</span>
                    </div>

                    {r.status === "QUOTED" && r.quote && (
                      <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl p-3" data-testid={`req-quote-${r.id}`}>
                        <div className="grid grid-cols-2 gap-y-1 text-sm">
                          <span className="text-stone-500">Harga satuan</span><span className="text-right font-semibold">{rupiah(r.quote.unit_price_idr)}</span>
                          <span className="text-stone-500">{r.quote.qty} pcs</span><span className="text-right">{rupiah(r.quote.unit_price_idr * r.quote.qty)}</span>
                          <span className="text-stone-500">Estimasi ongkir</span><span className="text-right">{rupiah(r.quote.shipping_estimate_idr)}</span>
                          <span className="font-semibold text-stone-700 pt-1 border-t border-emerald-100 mt-1">Total</span><span className="text-right font-display font-bold text-emerald-700 pt-1 border-t border-emerald-100 mt-1">{rupiah(r.quote.total_idr)}</span>
                        </div>
                        {r.quote.admin_note && <p className="text-xs text-stone-500 mt-2">Catatan admin: {r.quote.admin_note}</p>}
                        <div className="flex gap-2 mt-3">
                          <button data-testid={`req-accept-${r.id}`} onClick={() => setAcceptFor(r.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 rounded-lg">Terima & Buat Pesanan</button>
                          <button data-testid={`req-reject-${r.id}`} onClick={() => cancel(r.id)} className="px-3 border border-stone-200 rounded-lg text-sm text-stone-500">Tolak</button>
                        </div>
                      </div>
                    )}
                    {r.status === "ACCEPTED" && r.order_id && (
                      <button onClick={() => nav(`/pesanan/${r.order_id}`)} className="mt-2 text-sm font-semibold text-emerald-700 underline">Lihat pesanan →</button>
                    )}
                    {r.status === "PENDING" && (
                      <button data-testid={`req-cancel-${r.id}`} onClick={() => cancel(r.id)} className="mt-2 text-xs text-stone-400 hover:text-red-600">Batalkan permintaan</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Accept modal */}
      {acceptFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAcceptFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="accept-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display font-bold text-lg">Konfirmasi Pesanan</h3><button onClick={() => setAcceptFor(null)}><X size={20} className="text-stone-400" /></button></div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button data-testid="accept-kedai" onClick={() => setAccept({ ...accept, fulfillment: "kedai" })} className={`flex items-center gap-2 p-3 rounded-xl border-2 text-sm ${accept.fulfillment === "kedai" ? "border-emerald-600 bg-emerald-50" : "border-stone-200"}`}><Store size={18} className="text-emerald-600" />Ambil di Kedai</button>
              <button data-testid="accept-delivery" onClick={() => setAccept({ ...accept, fulfillment: "delivery" })} className={`flex items-center gap-2 p-3 rounded-xl border-2 text-sm ${accept.fulfillment === "delivery" ? "border-emerald-600 bg-emerald-50" : "border-stone-200"}`}><Truck size={18} className="text-emerald-600" />Kirim Alamat</button>
            </div>
            {accept.fulfillment === "kedai" ? (
              <select data-testid="accept-kiosk" value={accept.kiosk_id} onChange={(e) => setAccept({ ...accept, kiosk_id: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm bg-white mb-3">
                {kiosks.map((k) => <option key={k.id} value={k.id}>{k.name} — {k.subdistrict}, {k.city}</option>)}
              </select>
            ) : (
              <textarea data-testid="accept-address" value={accept.address} onChange={(e) => setAccept({ ...accept, address: e.target.value })} rows={2} placeholder="Alamat lengkap" className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm mb-3" />
            )}
            <input data-testid="accept-phone" value={accept.phone} onChange={(e) => setAccept({ ...accept, phone: e.target.value })} placeholder="No. HP / WhatsApp" className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm mb-4" />
            <button data-testid="accept-confirm" onClick={doAccept} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg">Buat Pesanan</button>
          </div>
        </div>
      )}
    </div>
  );
}
